import {
  Permission,
  type App,
  type ChainInfo,
  type NetworkInfo,
} from "@alch/dx-entities";
import { Link, Tooltip } from "@alch/ui";
import NetworkEmblem from "@components/ui/icons/NetworkEmblem";
import { useUpdateAppNetworkAllowlist } from "@features/app-details-multi-network/networks/useUpdateAppNetworkAllowlist";
import useHasPermission from "@queries/useHasPermission";
import networkNameWithoutChain from "@util/networkNameWithoutChain";
import clsx from "clsx";

interface NetworkItemProps {
  networkInfo: NetworkInfo;
  chainInfo?: ChainInfo;
  app?: App;
  disabled?: boolean;
}

const NetworkItem = ({
  networkInfo,
  chainInfo,
  disabled,
  app,
}: NetworkItemProps) => {
  // Show the network name without chain name if optional chainInfo is provided
  const networkName = chainInfo
    ? networkNameWithoutChain({ networkInfo, chainInfo })
    : networkInfo.name;

  const mutation = useUpdateAppNetworkAllowlist({ scopeId: app?.sid });
  const canEditAppConfig = useHasPermission(Permission.EditAppConfig);

  return (
    <div className="flex items-center gap-4">
      <div
        className={clsx(
          "flex items-center gap-2",
          disabled ? "opacity-50" : undefined,
        )}
      >
        <NetworkEmblem networkInfo={networkInfo} className="size-5" />

        <span className="text-secondary">{networkName}</span>
      </div>

      {app && disabled ? (
        <Tooltip
          content="You don't have permission to do this"
          disabled={canEditAppConfig}
          withArrow
        >
          <Link
            size="xs"
            disabled={!canEditAppConfig || mutation.isPending}
            onClick={() => {
              if (!canEditAppConfig) return;
              mutation.mutate({
                appId: app.sid,
                networkIds: [...app.networkAllowlist, networkInfo.id],
              });
            }}
          >
            Enable
          </Link>
        </Tooltip>
      ) : null}
    </div>
  );
};

export default NetworkItem;
